import { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import {
	Alert,
	Box,
	Button,
	Paper,
	Stack,
	Typography,
} from "@mui/material";
import {
	CalendarDays,
	CheckCircle2,
	ChevronDown,
	ChevronRight,
	FileText,
	Flame,
	List,
	Star,
	Target,
} from "lucide-react";
import { getRecentActivityPageData } from "utils/supabase";
import LogoLoader from "components/Custom/LogoLoader.js";

const typeConfig = {
	exam: {
		label: "Simuladores",
		icon: CheckCircle2,
		color: "#7c3aed",
		softBg: "#f3e8ff",
		badgeBg: "#efe7ff",
	},
	practice: {
		label: "Practicas",
		icon: FileText,
		color: "#2563eb",
		softBg: "#dbeafe",
		badgeBg: "#eaf3ff",
	},
	flashcards: {
		label: "Flashcards",
		icon: Star,
		color: "#f97316",
		softBg: "#ffedd5",
		badgeBg: "#fff1df",
	},
	default: {
		label: "Actividad",
		icon: Target,
		color: "#64748b",
		softBg: "#f1f5f9",
		badgeBg: "#f8fafc",
	},
};

const filters = [
	{ key: "all", label: "Todo", icon: List },
	{ key: "exam", label: "Simuladores", icon: CheckCircle2 },
	{ key: "practice", label: "Practica", icon: FileText },
	{ key: "flashcards", label: "Flashcards", icon: Star },
	{ key: "completed", label: "Completadas", icon: CheckCircle2 },
];

function formatInteger(value) {
	return new Intl.NumberFormat("es-MX").format(Number(value) || 0);
}

function clampPercent(value) {
	const parsed = Number(value);
	if (!Number.isFinite(parsed)) return 0;
	return Math.max(0, Math.min(100, Math.round(parsed)));
}

function normalizeType(type) {
	const normalized = String(type || "").toLowerCase();
	if (normalized.includes("flashcard")) return "flashcards";
	if (normalized.includes("practice") || normalized.includes("practica")) return "practice";
	if (normalized.includes("exam") || normalized.includes("simulador")) return "exam";
	return normalized || "default";
}

function isCompleted(item) {
	const status = String(item?.status || "").toLowerCase();
	return Boolean(
		item?.is_completed ||
		item?.completed_at ||
		item?.finished_at ||
		item?.ended_at ||
		status === "completed" ||
		status === "finished" ||
		status === "finalizado" ||
		status === "finalizada"
	);
}

function normalizeRoute(route, fallback = "/admin/dashboard") {
	if (!route) return fallback;
	if (route.startsWith("/admin/")) return route;
	if (route.startsWith("/simulador/results/")) {
		return route.replace("/simulador/results/", "/admin/simulador/").concat("/resultados");
	}
	if (route.startsWith("/simulador/")) return `/admin${route}`;
	if (route.startsWith("/practica")) return `/admin${route}`;
	if (route.startsWith("/flashcards")) return `/admin${route}`;
	if (route.startsWith("/")) return route.startsWith("/admin") ? route : `/admin${route}`;
	return fallback;
}

function normalizeActivityRoute(item) {
	const type = normalizeType(item?.type);
	const entityId = item?.entity_id;

	if (type === "exam" && entityId) {
		return isCompleted(item) ? `/admin/simulador/${entityId}/resultados` : `/admin/simulador/${entityId}`;
	}

	if (type === "flashcards" && entityId) {
		return isCompleted(item) ? `/admin/flashcards/${entityId}/resultados` : `/admin/flashcards/${entityId}`;
	}

	if (type === "practice" && entityId) {
		return `/admin/practica/${entityId}`;
	}

	return normalizeRoute(item?.route);
}

function SectionCard({ children, sx }) {
	return (
		<Paper
			elevation={0}
			sx={{
				border: "1px solid #e2e8f0",
				borderRadius: "14px",
				boxShadow: "0 16px 42px rgba(15, 23, 42, 0.06)",
				backgroundColor: "#fff",
				height: "100%",
				...sx,
			}}
		>
			{children}
		</Paper>
	);
}

function SummaryCard({ type, data }) {
	const config = typeConfig[type] || typeConfig.default;
	const Icon = config.icon;
	const average = clampPercent(data?.average_score);

	return (
		<SectionCard sx={{ p: { xs: 1.75, md: 2 } }}>
			<Stack direction="row" alignItems="center" spacing={{ xs: 2, md: 1.15, lg: 2 }} sx={{ minWidth: 0 }}>
				<Box sx={{ alignItems: "center", bgcolor: config.softBg, borderRadius: "50%", color: config.color, display: "flex", flexShrink: 0, height: { xs: 54, md: 42, lg: 54 }, justifyContent: "center", width: { xs: 54, md: 42, lg: 54 } }}>
					<Icon size={22} strokeWidth={2.4} fill={type === "flashcards" ? config.color : "none"} />
				</Box>
				<Box sx={{ flex: 1, minWidth: 0 }}>
					<Typography sx={{ color: "#0f172a", fontSize: { xs: 13, md: 11.5, lg: 13 }, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{config.label}</Typography>
					<Typography sx={{ color: "#0f172a", fontSize: { xs: 28, md: 22, lg: 28 }, fontWeight: 700, lineHeight: 1.1 }}>{formatInteger(data?.count)}</Typography>
				</Box>
				<Box sx={{ alignItems: "center", bgcolor: config.badgeBg, borderRadius: "8px", color: config.color, display: "flex", flexDirection: "column", flexShrink: 0, justifyContent: "center", px: { xs: 1.2, md: 0.45, lg: 1.2 }, py: { xs: 0.8, md: 0.5, lg: 0.8 }, textAlign: "center" }}>
					<Typography sx={{ fontSize: { xs: 16, md: 11.5, lg: 16 }, fontWeight: 700, lineHeight: 1 }}>{average}%</Typography>
					<Typography sx={{ color: "#64748b", fontSize: { xs: 10, md: 8, lg: 10 }, fontWeight: 500, mt: 0.25 }}>promedio</Typography>
				</Box>
			</Stack>
		</SectionCard>
	);
}

function FilterBar({ activeFilter, onChange }) {
	return (
		<Stack direction="row" spacing={1.2} sx={{ overflowX: "auto", pb: 0.5 }}>
			{filters.map((filter) => {
				const Icon = filter.icon;
				const active = activeFilter === filter.key;
				return (
					<Button
						key={filter.key}
						variant={active ? "contained" : "outlined"}
						onClick={() => onChange(filter.key)}
						startIcon={<Icon size={16} />}
						sx={{
							bgcolor: active ? "#7c3aed" : "#fff",
							borderColor: active ? "#7c3aed" : "#e2e8f0",
							borderRadius: "8px",
							boxShadow: active ? "0 10px 18px rgba(124, 58, 237, 0.22)" : "none",
							color: active ? "#fff" : "#334155",
							flexShrink: 0,
							fontSize: 12,
							fontWeight: 600,
							px: 2,
							py: 0.95,
							textTransform: "none",
							"&:hover": { bgcolor: active ? "#6d28d9" : "#f8fafc", borderColor: active ? "#6d28d9" : "#cbd5e1" },
						}}
					>
						{filter.label}
					</Button>
				);
			})}
		</Stack>
	);
}

function ActivityStatusBadge({ item }) {
	const completed = isCompleted(item);
	const status = String(item?.status || "").toLowerCase();
	const statusLabel = String(item?.status_label || "").toLowerCase();
	const statusLabels = {
		completed: "Completado",
		finished: "Completado",
		in_progress: "En progreso",
		draft: "En progreso",
		active: "En progreso",
		pending: "Pendiente",
	};
	const label = statusLabels[statusLabel] || statusLabels[status] || item?.status_label || (completed ? "Finalizado" : "En progreso");
	const color = completed ? "#16a34a" : "#2563eb";
	const bg = completed ? "#dcfce7" : "#dbeafe";

	return (
		<Box sx={{ bgcolor: bg, borderRadius: "7px", color, display: "inline-flex", fontSize: 11, fontWeight: 600, lineHeight: 1, px: 1, py: 0.65 }}>
			{label}
		</Box>
	);
}

function ActivityList({ items, onNavigate, onLoadMore, isLoadingMore }) {
	const groups = useMemo(() => {
		const map = new Map();
		(items || []).forEach((item) => {
			const group = item?.group_label || "Actividad";
			if (!map.has(group)) map.set(group, []);
			map.get(group).push(item);
		});
		return Array.from(map.entries());
	}, [items]);

	if (groups.length === 0) {
		return (
			<SectionCard sx={{ alignItems: "center", display: "flex", justifyContent: "center", minHeight: 260, p: 3 }}>
				<Typography sx={{ color: "#64748b", fontSize: 13, fontWeight: 500 }}>No hay actividad para este filtro.</Typography>
			</SectionCard>
		);
	}

	return (
		<SectionCard sx={{ overflow: "hidden", p: { xs: 1.25, md: 2 } }}>
			{groups.map(([group, rows]) => (
				<Box key={group} sx={{ mb: 1.5 }}>
					<Typography sx={{ color: "#0f172a", fontSize: 14, fontWeight: 600, mb: 0.8, px: 0.4 }}>{group}</Typography>
					{rows.map((item) => {
						const type = normalizeType(item?.type);
						const config = typeConfig[type] || typeConfig.default;
						const Icon = config.icon;
						const score = Number.isFinite(Number(item?.score_percentage)) ? `${Math.round(Number(item.score_percentage))}%` : "--";

						return (
							<Box
								key={item?.id || `${item?.type}-${item?.entity_id}-${item?.title}`}
								onClick={() => onNavigate(item)}
								sx={{
									alignItems: "center",
									borderTop: "1px solid #e2e8f0",
									cursor: "pointer",
									display: "grid",
									gap: { xs: 1, md: 1.5 },
									gridTemplateColumns: { xs: "42px minmax(0, 1fr) auto", md: "46px minmax(0, 1fr) 120px 112px 76px" },
									minHeight: 64,
									px: { xs: 0.2, md: 0.4 },
									py: 1,
									"&:hover": { backgroundColor: "#f8fafc" },
								}}
							>
								<Box sx={{ alignItems: "center", bgcolor: config.softBg, borderRadius: "50%", color: config.color, display: "flex", height: 36, justifyContent: "center", width: 36 }}>
									<Icon size={20} fill={type === "flashcards" ? config.color : "none"} />
								</Box>
								<Box sx={{ minWidth: 0 }}>
									<Typography sx={{ color: "#0f172a", fontSize: 13, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item?.title || config.label}</Typography>
									<Typography sx={{ color: "#64748b", fontSize: 12, fontWeight: 500, mt: 0.25, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item?.subtitle || "Sin detalles"}</Typography>
								</Box>
								<Typography sx={{ color: "#475569", display: { xs: "none", md: "block" }, fontSize: 12, fontWeight: 500 }}>{item?.time_label || item?.created_label || ""}</Typography>
								<Box sx={{ display: { xs: "none", md: "block" } }}><ActivityStatusBadge item={item} /></Box>
								<Box sx={{ bgcolor: config.badgeBg, borderRadius: "7px", color: config.color, fontSize: 13, fontWeight: 600, justifySelf: "end", minWidth: 58, px: 1, py: 0.7, textAlign: "center" }}>{score}</Box>
							</Box>
						);
					})}
				</Box>
			))}
			<Button
				disabled={isLoadingMore}
				onClick={onLoadMore}
				endIcon={<ChevronDown size={16} />}
				sx={{
					color: "#7c3aed",
					display: "flex",
					fontSize: 12,
					fontWeight: 600,
					justifyContent: "center",
					mx: "auto",
					pt: 1,
					textTransform: "none",
					"&:hover": { backgroundColor: "transparent", color: "#6d28d9" },
				}}
			>
				{isLoadingMore ? "Cargando..." : "Ver mas actividad"}
			</Button>
		</SectionCard>
	);
}

function DonutChart({ items, total }) {
	let current = 0;
	const segments = (items || []).map((item) => {
		const type = normalizeType(item?.type);
		const percentage = clampPercent(item?.percentage);
		const start = current;
		current += percentage;
		return `${(typeConfig[type] || typeConfig.default).color} ${start}% ${current}%`;
	});
	const gradient = segments.length > 0 ? segments.join(", ") : "#e2e8f0 0% 100%";

	return (
		<SectionCard sx={{ p: 2 }}>
			<Typography sx={{ color: "#0f172a", fontSize: 15, fontWeight: 600, mb: 2 }}>Actividad por tipo</Typography>
			<Stack direction={{ xs: "column", sm: "row", lg: "column", xl: "row" }} alignItems="center" spacing={2}>
				<Box sx={{ display: "flex", justifyContent: "center", width: { xs: "100%", sm: "auto", lg: "100%", xl: "auto" } }}>
					<Box sx={{ alignItems: "center", background: `conic-gradient(${gradient})`, borderRadius: "50%", display: "flex", height: 150, justifyContent: "center", width: 150 }}>
						<Box sx={{ alignItems: "center", bgcolor: "#fff", borderRadius: "50%", display: "flex", flexDirection: "column", height: 96, justifyContent: "center", width: 96 }}>
							<Typography sx={{ color: "#0f172a", fontSize: 22, fontWeight: 700, lineHeight: 1 }}>{formatInteger(total)}</Typography>
							<Typography sx={{ color: "#475569", fontSize: 11, fontWeight: 600, textAlign: "center" }}>actividades</Typography>
						</Box>
					</Box>
				</Box>
				<Stack spacing={1.2} sx={{ flex: 1, width: "100%" }}>
					{(items || []).map((item) => {
						const type = normalizeType(item?.type);
						const config = typeConfig[type] || typeConfig.default;
						return (
							<Stack key={item?.type || item?.label} direction="row" alignItems="center" spacing={1}>
								<Box sx={{ bgcolor: config.color, borderRadius: "50%", height: 9, width: 9 }} />
								<Typography sx={{ color: "#334155", flex: 1, fontSize: 12, fontWeight: 500 }}>{item?.label || config.label}</Typography>
								<Typography sx={{ color: "#475569", fontSize: 12, fontWeight: 600 }}>{clampPercent(item?.percentage)}% ({formatInteger(item?.count)})</Typography>
							</Stack>
						);
					})}
				</Stack>
			</Stack>
			<Stack direction="row" alignItems="center" spacing={1} sx={{ color: "#64748b", mt: 2 }}>
				<CalendarDays size={15} />
				<Typography sx={{ fontSize: 12, fontWeight: 500 }}>Periodo: Ultimos 7 dias</Typography>
			</Stack>
		</SectionCard>
	);
}

// Fixed actions/routes independent of the backend payload, since quick_actions.route isn't reliable.
const quickActionItems = [
	{ key: "exam", label: "Ver simuladores", description: "Prueba tus conocimientos", route: "/admin/examenes" },
	{ key: "practice", label: "Ir a práctica", description: "Responder preguntas", route: "/admin/practica" },
	{ key: "flashcards", label: "Abrir flashcards", description: "Repasar tarjetas", route: "/admin/flashcards" },
];

function QuickActions() {
	const history = useHistory();

	return (
		<SectionCard sx={{ p: 2 }}>
			<Typography sx={{ color: "#0f172a", fontSize: 15, fontWeight: 600, mb: 1.5 }}>Acciones rapidas</Typography>
			<Stack spacing={1}>
				{quickActionItems.map((action) => {
					const config = typeConfig[action.key] || typeConfig.default;
					const Icon = config.icon;
					return (
						<Button key={action.key} onClick={() => history.push(action.route)} sx={{ bgcolor: config.badgeBg, borderRadius: "10px", color: config.color, justifyContent: "space-between", p: 1.25, textAlign: "left", textTransform: "none", "&:hover": { bgcolor: config.softBg } }}>
							<Stack direction="row" alignItems="center" spacing={1.2} sx={{ minWidth: 0 }}>
								<Box sx={{ alignItems: "center", bgcolor: config.softBg, borderRadius: "8px", display: "flex", flexShrink: 0, height: 34, justifyContent: "center", width: 34 }}><Icon size={19} fill={config === typeConfig.flashcards ? config.color : "none"} /></Box>
								<Box sx={{ minWidth: 0 }}>
									<Typography sx={{ color: config.color, fontSize: 12, fontWeight: 700, lineHeight: 1.1 }}>{action.label}</Typography>
									<Typography sx={{ color: "#64748b", fontSize: 10.5, fontWeight: 500, mt: 0.25 }}>{action.description}</Typography>
								</Box>
							</Stack>
							<ChevronRight size={17} />
						</Button>
					);
				})}
			</Stack>
		</SectionCard>
	);
}

function StreakCard({ streak }) {
	const week = streak?.week_activity || [];
	return (
		<SectionCard sx={{ p: 2 }}>
			<Typography sx={{ color: "#0f172a", fontSize: 15, fontWeight: 600, mb: 1.5 }}>Racha actual</Typography>
			<Stack direction="row" alignItems="center" spacing={1.5}>
				<Box sx={{ alignItems: "center", bgcolor: "#ffedd5", borderRadius: "50%", color: "#f97316", display: "flex", height: 48, justifyContent: "center", width: 48 }}><Flame size={27} fill="#f97316" /></Box>
				<Box sx={{ flex: 1 }}>
					<Typography sx={{ color: "#0f172a", fontSize: 23, fontWeight: 700, lineHeight: 1 }}>{formatInteger(streak?.current_streak)} dias</Typography>
					<Typography sx={{ color: "#64748b", fontSize: 12, fontWeight: 500, mt: 0.25 }}>Sigue asi!</Typography>
				</Box>
				<Stack direction="row" spacing={0.65} sx={{ alignItems: "flex-start" }}>
					{week.map((day) => (
						<Stack key={day?.date || day?.label} alignItems="center" spacing={0.45} sx={{ width: 18 }}>
							<Typography sx={{ color: "#64748b", fontSize: 10, fontWeight: 600, lineHeight: 1, textAlign: "center", width: "100%" }}>{day?.label}</Typography>
							<Box sx={{ alignItems: "center", bgcolor: day?.has_activity ? "#22c55e" : "#f8fafc", border: day?.has_activity ? "none" : "1px solid #cbd5e1", borderRadius: "50%", color: "#fff", display: "flex", height: 18, justifyContent: "center", width: 18 }}>
								{day?.has_activity ? <CheckCircle2 size={13} /> : null}
							</Box>
						</Stack>
					))}
				</Stack>
			</Stack>
		</SectionCard>
	);
}

function TipCard() {
	return (
		<SectionCard sx={{ bgcolor: "#f5f3ff", p: 2 }}>
			<Stack direction="row" spacing={1.4}>
				<Box sx={{ alignItems: "center", border: "2px solid #7c3aed", borderRadius: "50%", color: "#7c3aed", display: "flex", flexShrink: 0, height: 34, justifyContent: "center", width: 34 }}>i</Box>
				<Box sx={{ flex: 1, minWidth: 0 }}>
					<Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
						<Typography sx={{ color: "#312e81", fontSize: 13, fontWeight: 700 }}>Consejo</Typography>
					</Stack>
					<Typography sx={{ color: "#64748b", fontSize: 11.5, fontWeight: 500, mt: 0.45 }}>Revisa tu actividad regularmente para identificar tus temas fuertes y areas de oportunidad.</Typography>
				</Box>
			</Stack>
		</SectionCard>
	);
}

export default function Actividad() {
	const history = useHistory();
	const [payload, setPayload] = useState(null);
	const [isLoading, setIsLoading] = useState(true);
	const [isLoadingMore, setIsLoadingMore] = useState(false);
	const [errorMessage, setErrorMessage] = useState("");
	const [activeFilter, setActiveFilter] = useState("all");
	const [activityLimit, setActivityLimit] = useState(10);
	const [showSplash, setShowSplash] = useState(true);

	useEffect(() => {
		let mounted = true;

		async function fetchActivity() {
			setIsLoading(true);
			setErrorMessage("");

			try {
				const { data, error } = await getRecentActivityPageData(7, 10);
				if (!mounted) return;

				if (error) {
					console.error("Error fetching activity page data:", error);
					setErrorMessage(error?.message || "No se pudo cargar la actividad.");
					setPayload(null);
					return;
				}

				setPayload(data || {});
			} catch (error) {
				if (!mounted) return;
				console.error("Unexpected error fetching activity page data:", error);
				setErrorMessage(error?.message || "No se pudo cargar la actividad.");
				setPayload(null);
			} finally {
				if (mounted) setIsLoading(false);
			}
		}

		fetchActivity();

		return () => {
			mounted = false;
		};
	}, []);

	useEffect(() => {
		if (isLoading) return undefined;

		// let the logo mouth-open animation finish before revealing the app
		const timeoutId = setTimeout(() => setShowSplash(false), 950);
		return () => clearTimeout(timeoutId);
	}, [isLoading]);

	const summary = payload?.summary || {};
	const recentActivity = Array.isArray(payload?.recent_activity) ? payload.recent_activity : [];
	const filteredActivity = recentActivity.filter((item) => {
		const type = normalizeType(item?.type);
		if (activeFilter === "all") return true;
		if (activeFilter === "completed") return isCompleted(item);
		return type === activeFilter;
	});

	const handleNavigate = (route) => {
		history.push(route);
	};

	const handleLoadMore = async () => {
		const nextLimit = activityLimit + 20;
		setIsLoadingMore(true);
		setErrorMessage("");

		try {
			const { data, error } = await getRecentActivityPageData(7, nextLimit);
			if (error) {
				console.error("Error loading more activity:", error);
				setErrorMessage(error?.message || "No se pudo cargar mas actividad.");
				return;
			}

			setPayload(data || {});
			setActivityLimit(nextLimit);
		} catch (error) {
			console.error("Unexpected error loading more activity:", error);
			setErrorMessage(error?.message || "No se pudo cargar mas actividad.");
		} finally {
			setIsLoadingMore(false);
		}
	};

	return (
		<Box sx={{ pb: 3 }}>
			<Box sx={{ mb: 3 }}>
				<Typography sx={{ color: "#0f172a", fontSize: { xs: 26, md: 31 }, fontWeight: 700, lineHeight: 1.05 }}>Actividad</Typography>
				<Typography sx={{ color: "#64748b", fontSize: 14, fontWeight: 400, mt: 0.8 }}>
					Tu historial de actividad en ENARM Apex
				</Typography>
			</Box>
			{showSplash ? (
				<SectionCard sx={{ minHeight: 360, p: 4, width: "100%" }}>
					<div style={{ alignItems: "center", display: "flex", flexDirection: "column", justifyContent: "center", minHeight: 280, width: "100%" }}>
						<LogoLoader size={110} label="Cargando actividad..." fullScreen={false} done={!isLoading} />
					</div>
				</SectionCard>
			) : errorMessage ? (
				<Alert severity="error" sx={{ borderRadius: "12px" }}>{errorMessage}</Alert>
			) : (
				<Stack spacing={2}>
					<Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1fr) 320px" }, alignItems: "start" }}>
						<Stack spacing={1.4} sx={{ minWidth: 0 }}>
							<Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" }, "@media (min-width: 768px)": { gridTemplateColumns: "repeat(3, minmax(0, 1fr))" } }}>
								<SummaryCard type="exam" data={summary.simulators} />
								<SummaryCard type="practice" data={summary.practices} />
								<SummaryCard type="flashcards" data={summary.flashcards} />
							</Box>
							<FilterBar activeFilter={activeFilter} onChange={setActiveFilter} />
							<ActivityList items={filteredActivity} onNavigate={(item) => handleNavigate(normalizeActivityRoute(item))} onLoadMore={handleLoadMore} isLoadingMore={isLoadingMore} />
						</Stack>

						<Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))", lg: "1fr" }, "@media (min-width: 768px)": { gridTemplateColumns: "repeat(2, minmax(0, 1fr))" }, "@media (min-width: 1200px)": { gridTemplateColumns: "1fr" }, minWidth: 0 }}>
							<DonutChart items={payload?.activity_by_type || []} total={summary.total_activities} />
							<QuickActions />
							<StreakCard streak={payload?.study_streak || {}} />
							<TipCard />
						</Box>
					</Box>
				</Stack>
			)}
		</Box>
	);
}
